import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  attestOccurrence,
  createCompliancePeriod,
  generateOccurrences,
  getMe,
  getOccurrence,
  getPeriodTimeline,
  linkOccurrenceEvidence,
  listComplianceEvidence,
  listCompliancePeriods,
  unlinkOccurrenceEvidence,
  updateOccurrence,
  uploadComplianceEvidence,
  type OccurrenceResult,
  type TimelineCell,
} from "@/lib/api";
import { Modal } from "@/components/Modal";

/**
 * The operating period — the thing a SOC 2 Type II audit actually tests.
 * One row per control, one cell per due occurrence: did it run, on time,
 * with evidence, checked by a second person?
 */
export function ComplianceOperatingPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const { data: periods } = useQuery({
    queryKey: ["compliance-periods"],
    queryFn: listCompliancePeriods,
  });
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });

  // Default to the period containing today, else the most recent.
  const activeId = useMemo(() => {
    if (periodId) return periodId;
    if (!periods || periods.length === 0) return null;
    const t = new Date().toISOString().slice(0, 10);
    return (periods.find((p) => p.startsOn <= t && t <= p.endsOn) ?? periods[0]).id;
  }, [periods, periodId]);

  const { data: timeline, isLoading } = useQuery({
    queryKey: ["compliance-timeline", activeId],
    queryFn: () => getPeriodTimeline(activeId!),
    enabled: !!activeId,
  });

  const generate = useMutation({
    mutationFn: () => generateOccurrences(activeId!),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["compliance-timeline", activeId] }),
  });

  const openId = params.get("open");

  return (
    <div>
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h1 className="page-title">Operating record</h1>
          <p className="mt-2 text-text-secondary">
            Each cell is one operation of one control: run, evidenced, and reviewed by a
            second person. This grid is what a Type II auditor samples.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {periods && periods.length > 0 && (
            <select
              value={activeId ?? ""}
              onChange={(e) => setPeriodId(e.target.value)}
              className="rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-heading"
            >
              {periods.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.startsOn} → {p.endsOn})
                </option>
              ))}
            </select>
          )}
          {activeId && (
            <button
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
              className="rounded-token-md border border-border bg-bg-card px-3 py-2 text-sm font-medium text-text-heading transition-colors hover:bg-bg-card-hover disabled:opacity-60"
              title="Create missing occurrences from each control's frequency"
            >
              Generate occurrences
            </button>
          )}
          {me?.role === "admin" && (
            <button
              onClick={() => setAddOpen(true)}
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
            >
              New period
            </button>
          )}
        </div>
      </header>

      {!activeId ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-center">
          <div className="max-w-md">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-token-lg bg-accent-muted text-2xl text-accent">
              <i className="fa-solid fa-calendar-check" />
            </div>
            <h2 className="font-display text-lg font-semibold text-text-heading">No period yet</h2>
            <p className="mx-auto mt-1.5 text-sm text-text-secondary">
              Create your observation window (e.g. a 12-month SOC 2 Type II period), then
              generate occurrences — every control gets its due dates and the quarterly
              rhythm starts.
            </p>
          </div>
        </div>
      ) : isLoading || !timeline ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : timeline.rows.length === 0 ? (
        <p className="rounded-token-lg border border-border bg-bg-card px-4 py-6 text-sm text-text-secondary">
          No occurrences yet — hit “Generate occurrences” to schedule every applicable
          control across this period.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-token-lg border border-border bg-bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                <th className="sticky left-0 bg-bg-card px-4 py-2.5 font-semibold">Control</th>
                <th className="px-4 py-2.5 font-semibold">Owner</th>
                <th className="px-4 py-2.5 font-semibold">Occurrences</th>
              </tr>
            </thead>
            <tbody>
              {timeline.rows.map((row) => (
                <tr key={row.controlId} className="border-b border-border-light">
                  <td className="sticky left-0 max-w-64 bg-bg-card px-4 py-2.5">
                    <span className="font-medium text-text-heading">{row.ref}</span>
                    <span className="ml-2 text-text-secondary">{row.name}</span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                    {row.owner?.name ?? <span className="text-warning">unassigned</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1.5">
                      {row.cells.map((cell) => (
                        <CellChip key={cell.id} cell={cell} onOpen={() => setParams({ open: cell.id })} />
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && <OccurrenceModal id={openId} onClose={() => setParams({})} />}
      {addOpen && <NewPeriodModal onClose={() => setAddOpen(false)} onCreated={(id) => setPeriodId(id)} />}
    </div>
  );
}

function CellChip({ cell, onOpen }: { cell: TimelineCell; onOpen: () => void }) {
  const cls = cell.overdue
    ? "bg-danger/15 text-danger"
    : cell.exception
      ? "bg-warning/15 text-warning"
      : cell.stage === "closed"
        ? "bg-success/15 text-success"
        : cell.stage === "prepared"
          ? "bg-accent-muted text-accent"
          : "border border-border text-text-secondary";
  const icon = cell.overdue
    ? "fa-triangle-exclamation"
    : cell.exception
      ? "fa-circle-exclamation"
      : cell.stage === "closed"
        ? "fa-circle-check"
        : cell.stage === "prepared"
          ? "fa-hourglass-half"
          : "fa-circle";
  return (
    <button
      onClick={onOpen}
      title={`due ${cell.dueOn} · ${cell.stage}${cell.overdue ? " · OVERDUE" : ""}${
        cell.exception ? " · exception" : ""
      } · ${cell.evidenceCount} evidence`}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium transition-opacity hover:opacity-80 ${cls}`}
    >
      <i className={`fa-solid ${icon} text-[0.6rem]`} />
      {cell.dueOn.slice(5)}
    </button>
  );
}

function NewPeriodModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const year = new Date().getFullYear();
  const [name, setName] = useState(`FY${year + 1} observation period`);
  const [startsOn, setStartsOn] = useState(`${year}-10-01`);
  const [endsOn, setEndsOn] = useState(`${year + 1}-09-30`);

  const create = useMutation({
    mutationFn: () => createCompliancePeriod({ name: name.trim(), startsOn, endsOn }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ["compliance-periods"] });
      onCreated(p.id);
      onClose();
    },
  });

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
  return (
    <Modal
      open
      onClose={onClose}
      title="New observation period"
      footer={
        <button
          onClick={() => create.mutate()}
          disabled={!name.trim() || create.isPending}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          Create
        </button>
      }
    >
      <div className="space-y-3">
        <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-text-muted">
            Starts
            <input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} className={inputCls} />
          </label>
          <label className="text-xs text-text-muted">
            Ends
            <input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} className={inputCls} />
          </label>
        </div>
        {create.isError && <p className="text-xs text-danger">{(create.error as Error).message}</p>}
      </div>
    </Modal>
  );
}

function OccurrenceModal({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: view } = useQuery({ queryKey: ["occurrence", id], queryFn: () => getOccurrence(id) });
  const { data: allEvidence } = useQuery({
    queryKey: ["compliance-evidence"],
    queryFn: listComplianceEvidence,
  });

  const [form, setForm] = useState<{
    occurredOn: string;
    result: OccurrenceResult;
    populationSize: string;
    sampleSize: string;
    deviations: string;
    notes: string;
  } | null>(null);
  const [statement, setStatement] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [linkId, setLinkId] = useState("");

  useEffect(() => {
    if (!view) return;
    const o = view.occurrence;
    setForm({
      occurredOn: o.occurredOn ?? "",
      result: o.result,
      populationSize: o.populationSize?.toString() ?? "",
      sampleSize: o.sampleSize?.toString() ?? "",
      deviations: o.deviations?.toString() ?? "",
      notes: o.notes ?? "",
    });
  }, [view]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["occurrence", id] });
    void qc.invalidateQueries({ queryKey: ["compliance-timeline"] });
    void qc.invalidateQueries({ queryKey: ["compliance-overview"] });
    void qc.invalidateQueries({ queryKey: ["compliance-evidence"] });
  };
  const fail = (e: Error) => setError(e.message);

  const save = useMutation({
    mutationFn: () =>
      updateOccurrence(id, {
        occurredOn: form!.occurredOn || null,
        result: form!.result,
        populationSize: form!.populationSize === "" ? null : Number(form!.populationSize),
        sampleSize: form!.sampleSize === "" ? null : Number(form!.sampleSize),
        deviations: form!.deviations === "" ? null : Number(form!.deviations),
        notes: form!.notes || null,
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const attest = useMutation({
    mutationFn: (action: "prepared" | "reviewed") => attestOccurrence(id, action, statement),
    onSuccess: () => {
      setStatement("");
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const upload = useMutation({
    mutationFn: () => uploadComplianceEvidence({ file: file!, occurrenceId: id }),
    onSuccess: () => {
      setFile(null);
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const link = useMutation({
    mutationFn: () => linkOccurrenceEvidence(id, linkId),
    onSuccess: () => {
      setLinkId("");
      invalidate();
    },
    onError: fail,
  });

  const unlink = useMutation({
    mutationFn: (evidenceId: string) => unlinkOccurrenceEvidence(id, evidenceId),
    onSuccess: invalidate,
    onError: fail,
  });

  if (!view || !form) return null;
  const o = view.occurrence;
  const closed = view.stage === "closed";
  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
  const linkable = (allEvidence ?? []).filter(
    (e) => !view.evidence.some((v) => v.evidence.id === e.id),
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={`${o.control?.ref ?? ""} — due ${o.dueOn}`}
      wide
    >
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
              closed
                ? "bg-success/15 text-success"
                : view.stage === "prepared"
                  ? "bg-accent-muted text-accent"
                  : "border border-border text-text-secondary"
            }`}
          >
            {view.stage}
          </span>
          <span className="text-text-muted">
            {o.control?.name} · {o.period?.name}
          </span>
        </div>

        {view.blockers.length > 0 && (
          <ul className="space-y-1">
            {view.blockers.map((b) => (
              <li key={b.rule} className="text-warning">
                <code className="mr-2 rounded bg-bg-secondary px-1.5 py-0.5 text-xs">{b.rule}</code>
                {b.message}
              </li>
            ))}
          </ul>
        )}

        {o.control?.testProcedure && (
          <Section title="Test procedure">
            <p className="whitespace-pre-wrap text-text-secondary">{o.control.testProcedure}</p>
          </Section>
        )}

        <Section title="What happened">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <label className="text-xs text-text-muted">
              Operated on
              <input
                type="date"
                value={form.occurredOn}
                onChange={(e) => setForm({ ...form, occurredOn: e.target.value })}
                className={inputCls}
              />
            </label>
            <label className="text-xs text-text-muted">
              Result
              <select
                value={form.result}
                onChange={(e) => setForm({ ...form, result: e.target.value as OccurrenceResult })}
                className={inputCls}
              >
                <option value="pending">pending</option>
                <option value="effective">effective</option>
                <option value="exception">exception</option>
              </select>
            </label>
            <label className="text-xs text-text-muted">
              Population
              <input
                type="number"
                min={0}
                value={form.populationSize}
                onChange={(e) => setForm({ ...form, populationSize: e.target.value })}
                placeholder="e.g. 47"
                className={inputCls}
              />
            </label>
            <label className="text-xs text-text-muted">
              Sample
              <input
                type="number"
                min={0}
                value={form.sampleSize}
                onChange={(e) => setForm({ ...form, sampleSize: e.target.value })}
                className={inputCls}
              />
            </label>
            <label className="text-xs text-text-muted">
              Deviations
              <input
                type="number"
                min={0}
                value={form.deviations}
                onChange={(e) => setForm({ ...form, deviations: e.target.value })}
                className={inputCls}
              />
            </label>
          </div>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            placeholder="What was done, what was found, what any deviations were."
            className={`mt-3 ${inputCls}`}
          />
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="mt-2 rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            Save record
          </button>
          {closed && (
            <span className="ml-3 text-xs text-warning">
              Editing a reviewed occurrence re-opens it for review — nothing is deleted.
            </span>
          )}
        </Section>

        <Section title={`Evidence — ${view.evidence.length}`}>
          <ul className="space-y-1.5">
            {view.evidence.map(({ link: l, evidence: e }) => (
              <li key={l.id} className="flex items-center gap-2 text-text-secondary">
                <i className="fa-solid fa-paperclip text-text-muted" />
                <a
                  href={`/api/compliance/evidence/${e.id}/download`}
                  className="text-accent hover:underline"
                >
                  {e.name}
                </a>
                <span className="text-xs text-text-muted">
                  {e.capturedOn ? `captured ${e.capturedOn}` : "no capture date"} ·{" "}
                  {(e.size / 1024).toFixed(0)} KB · {e.sha256.slice(0, 10)}…
                </span>
                <button
                  onClick={() => unlink.mutate(e.id)}
                  className="ml-auto text-xs text-text-muted hover:text-danger"
                  title="Unlink from this occurrence"
                >
                  <i className="fa-solid fa-link-slash" />
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="text-xs text-text-secondary"
            />
            <button
              onClick={() => upload.mutate()}
              disabled={!file || upload.isPending}
              className="rounded-token-md bg-accent text-on-accent px-3 py-1.5 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              Upload & attach
            </button>
            {linkable.length > 0 && (
              <>
                <select
                  value={linkId}
                  onChange={(e) => setLinkId(e.target.value)}
                  className="rounded-md border border-border bg-bg-card px-2 py-1.5 text-xs text-text-heading"
                >
                  <option value="">— link existing evidence —</option>
                  {linkable.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => link.mutate()}
                  disabled={!linkId || link.isPending}
                  className="rounded-token-md border border-border bg-bg-card px-3 py-1.5 text-xs font-medium text-text-heading hover:bg-bg-card-hover disabled:opacity-60"
                >
                  Link
                </button>
              </>
            )}
          </div>
        </Section>

        {!closed && (
          <Section title="Sign-off">
            <div className="flex gap-2">
              <input
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
                placeholder={
                  view.stage === "open"
                    ? "e.g. Ran the Q3 access review across all five systems."
                    : "e.g. Checked the export against the review record."
                }
                className="min-w-0 flex-1 rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-heading placeholder:text-text-muted"
              />
              <button
                onClick={() => attest.mutate(view.stage === "open" ? "prepared" : "reviewed")}
                disabled={!statement.trim() || attest.isPending}
                className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {view.stage === "open" ? "Prepare" : "Review & close"}
              </button>
            </div>
          </Section>
        )}

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
        {title}
      </h3>
      {children}
    </div>
  );
}
